import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  GripHorizontal,
  Loader2,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react'
import { useSplitHeight } from '@/hooks/useSplitHeight'
import { useSoundEffects, useSoundOnMount } from '@/hooks/useSoundEffects'
import { usePdfDocument } from '@/hooks/usePdfDocument'
import { usePdfChapters } from '@/hooks/usePdfChapters'
import { NavProgressBar } from '@/components/NavProgressBar'
import { chapterAt } from '@/lib/pdfChapters'
import { pdfDownloadUrl, pdfProxyUrl, pdfSourceHost } from '@/lib/examPdf'
import {
  anchoredScroll,
  canvasPixelRatio,
  clampPage,
  clampZoom,
  fitWidthScale,
  nudgeZoom,
  pageFitZoom,
  pinchZoom,
  WIDTH_ZOOM,
} from '@/lib/pdfViewer'

/**
 * What the panel shows when it can't show the document. Every failure here —
 * the endpoint isn't deployed, the publisher moved the file, a page won't
 * render — leaves the reader the same two actions, so they share one card.
 */
function ReadingFailure({ message, url, sourceHost }: { message: string; url: string; sourceHost: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-[40px] items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent transition-colors"
        >
          Open at {sourceHost || 'the source'}
          <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
        </a>
        <a
          href={pdfDownloadUrl(url)}
          download
          className="inline-flex min-h-[40px] items-center gap-2 rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-accent/40 hover:text-foreground transition-colors"
        >
          <Download className="h-4 w-4 shrink-0" aria-hidden />
          Download
        </a>
      </div>
    </div>
  )
}

interface PdfDocumentViewProps {
  /** The publisher's URL for the PDF — casact.org / soa.org. */
  url: string
  /** What the document is, e.g. "Examiner's Report". */
  title: string
  /** Which paper it belongs to, e.g. "Exam 5 · Spring 2019". */
  subtitle?: string
  /** Whether the host fills the viewport, so the header joins a reading column. */
  focusMode: boolean
  /** The host's own header controls, after Download — its expand and close. */
  controls: ReactNode
  /**
   * Whether the view owns the arrow keys and `+`/`−` right now. A host that is
   * not the top layer — a stacked document with the app's reader over it —
   * hands them to whatever is.
   */
  keyboard?: boolean
  /** The page to open at — where a stacked document was left before it folded. */
  initialPage?: number
  /** Reports the page being read, so a host can put the reader back on it. */
  onPageChange?: (page: number) => void
}

/**
 * The exam-PDF reader: a past paper read *in* the app rather than in a browser
 * tab you then have to find your way back from.
 *
 * It reads like the concept popup — a header naming the document, the page,
 * and the same Previous / position / Next footer — because a source document is
 * another thing you read beside your work. Pages here are what concepts are
 * there.
 *
 * This is the reader itself, without a frame, and it has two: `PdfViewerPanel`
 * below, the free-standing slide-up panel every PDF button outside the concept
 * popup opens (`components/PdfReaderHost.tsx`), and a page of the popup's own
 * stack (`components/wiki/PdfPagePanel.tsx`), where a resource page's Read PDF
 * opens its document on top of the page, the way a followed link does. The
 * frame owns the rest: the resize handle, full screen, Esc, the opening sound.
 *
 * The pages are drawn by pdf.js rather than handed to a browser's PDF plugin
 * (`lib/pdfjsSetup.ts` explains why), and the bytes come from `quiz/api/exam-pdf.js`
 * rather than the publisher (`lib/examPdf.ts` explains why). Everything assumes
 * that can still fail — a moved file, an endpoint that isn't deployed — so the
 * publisher's own copy is always one tap away.
 */
export function PdfDocumentView({
  url,
  title,
  subtitle,
  focusMode,
  controls,
  keyboard = true,
  initialPage = 1,
  onPageChange,
}: PdfDocumentViewProps) {
  const { play } = useSoundEffects()

  const proxied = useMemo(() => pdfProxyUrl(url), [url])
  const { doc, pageCount, status, error } = usePdfDocument(proxied)
  // The document's own bookmarks, which cut the page bar into chapters. Empty
  // for anything that carries none — most of the scanned papers — and the bar
  // is then the plain strip it has always been.
  const chapters = usePdfChapters(doc)
  const chapterMarks = useMemo(
    () => chapters.map(chapter => ({ start: chapter.startPage, label: chapter.title })),
    [chapters],
  )

  // Two page numbers, for the same reason there are three zooms below: `page`
  // is where the reader is, live under their finger on the scrubber, and
  // `renderPage` is the one being drawn, which lags it until the scrub settles.
  // A drag across a 423-page report crosses a page every few milliseconds, and
  // parsing each one only to cancel it would leave the reader watching a
  // spinner instead of the page they stopped on.
  const [page, setPage] = useState(initialPage)
  const [renderPage, setRenderPage] = useState(initialPage)
  // Three zooms, because a page is a bitmap that takes a moment to redraw and
  // a pinch changes the zoom continuously:
  //   `zoom`       what the reader has asked for, live under their finger;
  //   `renderZoom` what the drawing effect is working towards, which lags the
  //                gesture until it settles (redrawing on every step of a pinch
  //                would queue dozens of cancelled renders);
  //   `sizedZoom`  what the drawn bitmap is sized for.
  // The gap between the first and the last is covered by scaling that bitmap
  // with a CSS transform, so the page grows under the finger and only goes
  // crisp once the render lands. The *layout* follows the zoom immediately
  // either way — see `pageBoxRef` — so the redraw changes sharpness and
  // nothing else.
  const [zoom, setZoom] = useState<number>(WIDTH_ZOOM)
  const [renderZoom, setRenderZoom] = useState<number>(WIDTH_ZOOM)
  const [sizedZoom, setSizedZoom] = useState<number>(WIDTH_ZOOM)
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null)
  // The page's own size, in PDF points. Measured before anything is drawn,
  // because it and the panel's shape are what the whole-page fit is made of.
  const [pageBase, setPageBase] = useState<{ width: number; height: number } | null>(null)
  const [rendering, setRendering] = useState(false)
  const [pageError, setPageError] = useState<string | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)
  // Whether the reader has set the zoom themselves. Until they have, the page
  // stays fitted to the panel and re-fits as the panel is resized.
  const [zoomed, setZoomed] = useState(false)
  const [dragging, setDragging] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // The box the page occupies in the layout. It is sized for the zoom the
  // reader has asked for, not for the bitmap inside it, which is what makes the
  // scrollable area grow with the gesture instead of at the end of it.
  const pageBoxRef = useRef<HTMLDivElement>(null)
  // The live zoom, readable from the gesture listeners, which are attached once
  // and would otherwise close over a stale one.
  const zoomRef = useRef(WIDTH_ZOOM)
  // Where the panel was looking when a zoom was asked for, so the same point
  // can be put back once the page has grown. Held from the event that changed
  // the zoom until the layout effect below consumes it.
  const zoomAnchorRef = useRef<{
    left: number
    top: number
    width: number
    height: number
    clientWidth: number
    clientHeight: number
    focus: { x: number; y: number } | null
  } | null>(null)
  const dragRef = useRef<{ id: number; x: number; y: number; left: number; top: number } | null>(null)
  const sourceHost = pdfSourceHost(url)

  useEffect(() => { zoomRef.current = zoom }, [zoom])
  const requestZoomRef = useRef<(next: number, focus?: { x: number; y: number } | null) => void>(() => {})

  /**
   * The bottom of the zoom range: the size at which the whole page is on
   * screen. On a phone the panel is about the shape of a page, so this is the
   * width fit (1×); on a desktop panel, which is a wide strip, it is well below
   * 1× — which is the point, since a page fitted to that width runs several
   * panel-heights down.
   */
  const minZoom = useMemo(
    () => (pageBase ? pageFitZoom(containerWidth, containerHeight, pageBase.width, pageBase.height) : WIDTH_ZOOM),
    [containerWidth, containerHeight, pageBase],
  )
  const minZoomRef = useRef(WIDTH_ZOOM)
  useEffect(() => { minZoomRef.current = minZoom }, [minZoom])

  /**
   * Sit at the fitted size until the reader says otherwise, and stay inside the
   * range when the panel is resized under a zoom they did set.
   *
   * `renderZoom` is set alongside `zoom` in the fitted case rather than left to
   * the settle timer below: this is the size the document opens at, so waiting
   * out the zoom debounce would draw the page at the wrong size first.
   */
  useEffect(() => {
    if (!zoomed) {
      zoomRef.current = minZoom
      setZoom(minZoom)
      setRenderZoom(minZoom)
      return
    }
    const clamped = clampZoom(zoomRef.current, minZoom)
    if (clamped !== zoomRef.current) {
      zoomRef.current = clamped
      setZoom(clamped)
    }
  }, [minZoom, zoomed])

  /**
   * Every zoom goes through here: the pinch, ctrl+wheel, the keys. There is no
   * zoom control in the chrome — the gestures are the zoom.
   *
   * The panel is measured *before* the change, while the DOM still shows the
   * old size, because that measurement is what the point being held still is
   * expressed against. `focus` is where the reader is looking — the midpoint
   * between their fingers in a pinch, the pointer under a trackpad zoom, and
   * the middle of the panel for a zoom that names no position of its own (the
   * +/- keys).
   */
  const requestZoom = useCallback((next: number, focus?: { x: number; y: number } | null) => {
    const target = clampZoom(next, minZoomRef.current)
    if (target === zoomRef.current) return
    setZoomed(true)
    const el = scrollRef.current
    if (el) {
      zoomAnchorRef.current = {
        left: el.scrollLeft,
        top: el.scrollTop,
        width: el.scrollWidth,
        height: el.scrollHeight,
        clientWidth: el.clientWidth,
        clientHeight: el.clientHeight,
        focus: focus ?? null,
      }
    }
    zoomRef.current = target
    setZoom(target)
  }, [])

  useEffect(() => { requestZoomRef.current = requestZoom }, [requestZoom])

  /**
   * Hold that point still, in the same frame the page changed size.
   *
   * A layout effect, so the scroll offset is corrected after the box has been
   * re-sized but before the browser paints: the page grows around what the
   * reader is looking at rather than around the top-left corner and a scroll
   * correction they can see happening.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current
    const anchor = zoomAnchorRef.current
    if (!el || !anchor) return
    zoomAnchorRef.current = null
    el.scrollLeft = anchoredScroll(
      anchor.left,
      anchor.focus?.x ?? anchor.clientWidth / 2,
      anchor.width,
      el.scrollWidth,
      anchor.clientWidth,
    )
    el.scrollTop = anchoredScroll(
      anchor.top,
      anchor.focus?.y ?? anchor.clientHeight / 2,
      anchor.height,
      el.scrollHeight,
      anchor.clientHeight,
    )
  }, [zoom])

  // A new document starts at its first page, fitted to the panel — the fit
  // itself lands once the page has been measured, below. Not on mount: the
  // state above already starts there, or on the page a folded document was
  // left on, which this would throw away.
  const shownRef = useRef(proxied)
  useEffect(() => {
    if (shownRef.current === proxied) return
    shownRef.current = proxied
    setPage(1)
    setRenderPage(1)
    setPageBase(null)
    setZoomed(false)
    zoomRef.current = WIDTH_ZOOM
    setZoom(WIDTH_ZOOM)
    setRenderZoom(WIDTH_ZOOM)
  }, [proxied])

  // Draw the page the reader has settled on. Short enough that a Previous /
  // Next press still starts drawing immediately as far as anyone can tell, long
  // enough that a scrub — which crosses pages faster than this the whole way —
  // renders once, where the finger stops, rather than at every page it passed.
  useEffect(() => {
    if (page === renderPage) return
    const timer = window.setTimeout(() => setRenderPage(page), 60)
    return () => window.clearTimeout(timer)
  }, [page, renderPage])

  // Follow the gesture once it stops moving. A pinch across the whole range
  // fires ~60 changes; each one would start a page render only to have it
  // cancelled by the next, so the reader would see the CSS preview and never a
  // sharp page.
  useEffect(() => {
    if (zoom === renderZoom) return
    const timer = window.setTimeout(() => setRenderZoom(zoom), 140)
    return () => window.clearTimeout(timer)
  }, [zoom, renderZoom])

  // Stepping pages is paper moving, not a press — the same `ruffle` the
  // concept popup's Previous/Next makes, and the same one the bar above this
  // footer makes when it's dragged.
  const turnPage = useCallback((direction: -1 | 1) => {
    setPage(current => {
      const next = clampPage(current + direction, pageCount)
      if (next !== current) play('ruffle')
      return next
    })
  }, [pageCount, play])

  const changeZoom = useCallback((direction: -1 | 1) => {
    requestZoom(nudgeZoom(zoomRef.current, direction, minZoomRef.current))
  }, [requestZoom])

  // The arrows turn pages and `+`/`−` zoom. Esc is the frame's: it is what
  // knows whether the key leaves full screen, closes a panel or unwinds a stack.
  useEffect(() => {
    if (!keyboard) return
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
      if (e.key === 'ArrowLeft') turnPage(-1)
      else if (e.key === 'ArrowRight') turnPage(1)
      else if (e.key === '+' || e.key === '=') changeZoom(1)
      else if (e.key === '-') changeZoom(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [keyboard, turnPage, changeZoom])

  // Recorded as the reader moves, not on the way out — the same rule the
  // concept pages' scroll memory keeps (`lib/pageScrollMemory.ts`).
  useEffect(() => {
    onPageChange?.(page)
  }, [page, onPageChange])

  // The panel is resizable and the sidebar collapses under it, so the page is
  // re-fitted to whatever width it actually has rather than measured once.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    setContainerWidth(el.clientWidth)
    setContainerHeight(el.clientHeight)
    const observer = new ResizeObserver(entries => {
      const box = entries[0]?.contentRect
      if (!box) return
      if (box.width > 0) setContainerWidth(box.width)
      if (box.height > 0) setContainerHeight(box.height)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [status])

  // Measure the page before drawing it. The fit — and so the zoom the document
  // opens at — is made of the page's proportions and the panel's, and drawing
  // first would show the page at the wrong size for a moment and then shrink
  // it. Pages of one document are almost always the same size, so the setter
  // keeps the old object when they are and nothing re-renders.
  useEffect(() => {
    if (!doc) return
    let cancelled = false
    void (async () => {
      try {
        const pdfPage = await doc.getPage(clampPage(renderPage, doc.numPages))
        if (cancelled) return
        const { width, height } = pdfPage.getViewport({ scale: 1 })
        setPageBase(prev => (prev && prev.width === width && prev.height === height ? prev : { width, height }))
      } catch {
        // The render effect below opens the same page and reports the failure.
      }
    })()
    return () => { cancelled = true }
  }, [doc, renderPage])

  // Draw the settled page. Re-runs on page, settled zoom and width; the previous
  // render is cancelled so a fast flick through pages can't paint an older page
  // over a newer one.
  useEffect(() => {
    // Nothing is drawn until the page is at the size it will open at: with the
    // fit known and the zoom not yet the reader's, `renderZoom` is one state
    // update behind the effect above, and drawing now would show a
    // fitted-width page for a moment and then shrink it.
    if (!doc || containerWidth <= 0 || !pageBase) return
    if (!zoomed && renderZoom !== minZoom) return
    let cancelled = false
    let task: { cancel: () => void } | null = null
    setRendering(true)
    setPageError(null)

    void (async () => {
      try {
        const pdfPage = await doc.getPage(clampPage(renderPage, doc.numPages))
        if (cancelled) return
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')
        // Nothing to draw on, or nothing to fit to. Bail out of the *spinner*
        // as well — a stuck "Rendering page…" reads as a hang.
        if (!canvas || !context) {
          setRendering(false)
          return
        }

        const fit = fitWidthScale(containerWidth, pdfPage.getViewport({ scale: 1 }).width)
        if (!fit) {
          setRendering(false)
          return
        }
        const scale = fit * renderZoom
        const viewport = pdfPage.getViewport({ scale })
        // The canvas is a bitmap: it's sized in device pixels for sharpness and
        // laid out in CSS pixels, with the ratio held above a real resolution
        // (a fitted page is only ~125 dpi at the screen's own ratio, which
        // scanned pages don't survive) and capped so a deep zoom on a retina
        // screen can't ask for a canvas the browser refuses to allocate.
        const ratio = canvasPixelRatio(viewport.width, viewport.height, window.devicePixelRatio, scale)
        const width = Math.floor(viewport.width)
        const height = Math.floor(viewport.height)
        canvas.width = Math.floor(viewport.width * ratio)
        canvas.height = Math.floor(viewport.height * ratio)
        canvas.style.width = `${width}px`
        canvas.style.height = `${height}px`
        // The bitmap now *is* the size the box has been laid out at, so the
        // transform that was standing in for it comes off, and the box is
        // pinned to the same size it already had. Both are written here rather
        // than left to the re-render below: that lands a frame later, and this
        // frame would otherwise paint a full-size page scaled up again on top
        // of a box that no longer matches it.
        canvas.style.transform = ''
        if (pageBoxRef.current) {
          pageBoxRef.current.style.width = `${width}px`
          pageBoxRef.current.style.height = `${height}px`
        }
        setSizedZoom(renderZoom)
        setPageSize({ width, height })
        // Sizing a canvas resets its context, so the filtering hint has to be
        // set after: whatever reduction is left when a 300 dpi scan meets this
        // canvas should be averaged rather than point-sampled.
        context.imageSmoothingEnabled = true
        context.imageSmoothingQuality = 'high'

        const renderTask = pdfPage.render({
          canvasContext: context,
          canvas,
          viewport,
          transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
        })
        task = renderTask
        await renderTask.promise
        if (!cancelled) {
          setRendering(false)
          setPageError(null)
        }
      } catch (err) {
        // Cancellation is the normal case here — the page changed under the
        // render — and is not a failure. Anything else is, and says so rather
        // than leaving an empty rectangle the reader has to interpret.
        if (cancelled || (err as { name?: string })?.name === 'RenderingCancelledException') return
        setRendering(false)
        setPageError(err instanceof Error ? err.message : 'Unknown error')
      }
    })()

    return () => {
      cancelled = true
      task?.cancel()
    }
  }, [doc, renderPage, renderZoom, containerWidth, pageBase, zoomed, minZoom])

  // Each page starts at its top — landing halfway down page 12 because page 11
  // was scrolled is disorienting. Keyed on the drawn page, so a scrub resets the
  // scroll once, with the page it lands on, rather than at every page it crosses.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [renderPage])

  /**
   * Panning, on the three things this is read on.
   *
   * A zoomed page is bigger than the panel in both directions, and reaching the
   * rest of it is the whole point of zooming. The page area is a scroll
   * container, so a finger already pans it natively (with momentum, which no
   * hand-rolled version matches) — `touch-action: pan-x pan-y` keeps that while
   * taking pinch away from the browser, which would otherwise zoom the whole
   * site rather than the document. That leaves the two the browser gives us
   * nothing for: a mouse, which gets grab-and-drag over the page, and a
   * trackpad's pinch, which arrives as ctrl+wheel.
   */
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    function distance(touches: TouchList) {
      const [a, b] = [touches[0], touches[1]]
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
    }

    function focusOf(touches: TouchList) {
      const rect = el!.getBoundingClientRect()
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
        y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top,
      }
    }

    let pinch: { distance: number; zoom: number } | null = null

    function onTouchStart(e: TouchEvent) {
      pinch = e.touches.length === 2
        ? { distance: distance(e.touches), zoom: zoomRef.current }
        : null
    }

    function onTouchMove(e: TouchEvent) {
      if (!pinch || e.touches.length !== 2) return
      // Non-passive so this can be prevented: two fingers on a scroll container
      // is a scroll gesture to the browser, and we want it to be a zoom.
      e.preventDefault()
      requestZoomRef.current(
        pinchZoom(pinch.zoom, pinch.distance, distance(e.touches), minZoomRef.current),
        focusOf(e.touches),
      )
    }

    function onTouchEnd(e: TouchEvent) {
      if (e.touches.length < 2) pinch = null
    }

    function onWheel(e: WheelEvent) {
      // ctrl+wheel is what a trackpad pinch and a browser zoom shortcut both
      // send. Over the document it should zoom the document.
      if (!e.ctrlKey) return
      e.preventDefault()
      const rect = el!.getBoundingClientRect()
      requestZoomRef.current(
        zoomRef.current * Math.exp(-e.deltaY / 200),
        { x: e.clientX - rect.left, y: e.clientY - rect.top },
      )
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchEnd, { passive: true })
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
      el.removeEventListener('wheel', onWheel)
    }
  }, [])

  // Grab-and-drag for a mouse. Only from the page itself: a drag started on the
  // failure card would capture the pointer and swallow the click on its links.
  function startPan(e: React.PointerEvent<HTMLDivElement>) {
    const el = scrollRef.current
    if (!el || e.pointerType === 'touch' || e.button !== 0) return
    if ((e.target as HTMLElement).tagName !== 'CANVAS') return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop }
    setDragging(true)
  }

  function pan(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current
    const el = scrollRef.current
    if (!drag || !el || drag.id !== e.pointerId) return
    el.scrollLeft = drag.left - (e.clientX - drag.x)
    el.scrollTop = drag.top - (e.clientY - drag.y)
  }

  function endPan(e: React.PointerEvent<HTMLDivElement>) {
    if (dragRef.current?.id !== e.pointerId) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    dragRef.current = null
    setDragging(false)
  }

  const position = pageCount > 0 ? `${clampPage(page, pageCount)} of ${pageCount}` : ''
  // Which chapter the page being read belongs to. The bar shows this under a
  // finger; the footer shows it while you read, which is the half of "where am
  // I" a page number can't answer.
  const currentChapter = chapterAt(chapters, clampPage(page, pageCount))?.title ?? ''
  // How far the drawn page has to be stretched to stand in for the zoom the
  // reader has asked for. 1 whenever the two agree, which is most of the time.
  const previewScale = sizedZoom > 0 ? zoom / sizedZoom : 1

  return (
    <>
      {/* Header: what you're reading, then save and the frame's own controls.
          Focus mode spans the viewport, so it shares the body's reading column. */}
      <div className={`flex items-center gap-2 h-16 shrink-0 ${focusMode ? 'w-full max-w-4xl mx-auto px-4 sm:px-6' : 'px-3'}`}>
        <div className="flex-1 min-w-0">
          <h2 className="truncate font-semibold text-lg sm:text-xl min-w-0">{title}</h2>
          {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <a
          href={pdfDownloadUrl(url)}
          download
          className="inline-flex items-center justify-center h-10 w-10 rounded-lg shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title="Download PDF"
          aria-label="Download PDF"
        >
          <Download className="h-5 w-5" />
        </a>
        {controls}
      </div>

      {/* The page. Scrolls in both directions, because a zoomed-in page is
          wider than the panel and panning it is the point of zooming.
          `pan-x pan-y` keeps a one-finger drag scrolling natively while
          reserving pinch for the zoom handler above. */}
      <div
        ref={scrollRef}
        className="relative flex-1 min-h-0 overflow-auto overscroll-contain bg-muted/40 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        style={{ touchAction: 'pan-x pan-y' }}
        onPointerDown={startPan}
        onPointerMove={pan}
        onPointerUp={endPan}
        onPointerCancel={endPan}
      >
        {status === 'error' ? (
          <ReadingFailure
            message={error ? `This document couldn't be loaded — ${error}.` : "This document couldn't be loaded."}
            url={url}
            sourceHost={sourceHost}
          />
        ) : (
          <div className="flex min-h-full w-full p-4">
            {/* The page's box in the layout, sized for the zoom that has been
                *asked* for — so the scrollable area grows with the gesture
                rather than in a step when the redraw lands, and the redraw
                itself changes nothing but sharpness.

                `m-auto` centres it rather than `justify-center` on the row:
                auto margins collapse to zero once the page outgrows the panel,
                where `justify-center` would push half of it off the left edge —
                into space a scroll container can't reach. */}
            <div
              ref={pageBoxRef}
              className="m-auto shrink-0 shadow-sm"
              style={pageSize ? {
                width: Math.round(pageSize.width * previewScale),
                height: Math.round(pageSize.height * previewScale),
              } : undefined}
            >
              <canvas
                ref={canvasRef}
                className="block max-w-none origin-top-left"
                style={{
                  // Between a zoom gesture and the redraw that follows it, the
                  // page on screen is the last bitmap stretched to fill the box
                  // above. Scaled from its top-left corner, which is where the
                  // box's own corner is, so every point of the page lands
                  // exactly where the redraw will put it.
                  transform: previewScale === 1 ? undefined : `scale(${previewScale})`,
                  cursor: dragging ? 'grabbing' : 'grab',
                }}
                aria-label={`Page ${renderPage}`}
              />
            </div>
          </div>
        )}

        {/* A page that wouldn't draw covers the canvas rather than replacing it:
            the canvas has to stay mounted, or turning to a page that *does*
            draw would have nothing to draw on. */}
        {pageError && status !== 'error' && (
          <div className="absolute inset-0 bg-card/95">
            <ReadingFailure
              message={`This page couldn't be drawn (${pageError}).`}
              url={url}
              sourceHost={sourceHost}
            />
          </div>
        )}

        {(status === 'loading' || rendering) && status !== 'error' && !pageError && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-card/90 px-3 py-1.5 text-sm text-muted-foreground shadow-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {status === 'loading' ? 'Loading document…' : 'Rendering page…'}
            </span>
          </div>
        )}
      </div>

      {/* How far through the document you are — and the way to move through it.
          "1 of 423" in the footer says where you are but gives no sense of how
          much is left, and Previous / Next is 422 presses from one end to the
          other. Drag the bar and the page follows, the same gesture as a video
          timeline; the bubble names the page you'd land on, because a fraction
          of a 423-page report isn't a page number.

          Where the document carries bookmarks, the bar is cut into them the way
          a video's timeline is cut into chapters: the segments show how the
          paper is laid out — that question 14 starts here and runs four pages —
          and the bubble names the one you're aiming at, so finding a question
          is a glance and a press rather than a hunt. */}
      {status === 'ready' && pageCount > 0 && (
        <NavProgressBar
          current={clampPage(page, pageCount)}
          total={pageCount}
          className="border-t"
          label={`Page ${clampPage(page, pageCount)} of ${pageCount}`}
          onScrub={next => setPage(clampPage(next, pageCount))}
          formatValue={n => `Page ${n} of ${pageCount}`}
          segments={chapterMarks}
        />
      )}

      {/* Footer: Previous / where you are / Next, as in the concept popup, with
          the way back to the publisher's copy. */}
      <div className="flex items-stretch h-16 shrink-0 border-t bg-background/60">
        <button
          type="button"
          disabled={status !== 'ready' || page <= 1}
          data-sound="none"
          onClick={() => turnPage(-1)}
          className="flex-1 flex items-center justify-center gap-2 px-4 text-base sm:text-sm font-medium hover:bg-accent/60 active:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-6 w-6 sm:h-5 sm:w-5" />
          <span>Previous</span>
        </button>

        <div className="self-center flex shrink-0 flex-col items-center px-2">
          {/* The section you're in, when the document says what its sections
              are. A page number on its own is a coordinate; this is the part
              that tells you whether you're where you meant to be. */}
          {currentChapter && (
            <span className="max-w-[7.5rem] sm:max-w-[14rem] truncate text-xs font-medium">
              {currentChapter}
            </span>
          )}
          <span className="text-sm sm:text-xs text-muted-foreground tabular-nums">
            {position || '—'}
          </span>
        </div>

        <button
          type="button"
          disabled={status !== 'ready' || page >= pageCount}
          data-sound="none"
          onClick={() => turnPage(1)}
          className="flex-1 flex items-center justify-center gap-2 px-4 text-base sm:text-sm font-medium hover:bg-accent/60 active:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
        >
          <span>Next</span>
          <ChevronRight className="h-6 w-6 sm:h-5 sm:w-5" />
        </button>
      </div>
    </>
  )
}

interface Props {
  /** The publisher's URL for the PDF — casact.org / soa.org. */
  url: string
  /** What the document is, e.g. "Examiner's Report". */
  title: string
  /** Which paper it belongs to, e.g. "Exam 5 · Spring 2019". */
  subtitle?: string
  /** The page to open at — the one a citation points to. */
  initialPage?: number
  onClose: () => void
}

/**
 * The free-standing reader: `PdfDocumentView` in the concept popup's shell —
 * the same slide-up bottom panel, the same drag-to-resize handle and shared
 * preferred height, the same focus-mode expand. Mounted once, by
 * `components/PdfReaderHost.tsx`, for every PDF button outside the popup; a
 * document opened from a page *inside* the popup is read in its stack instead
 * (`components/wiki/PdfPagePanel.tsx`).
 */
export function PdfViewerPanel({ url, title, subtitle, initialPage, onClose }: Props) {
  const { play } = useSoundEffects()
  // A sheet of paper sliding out, same as the concept popup opening.
  useSoundOnMount('open')
  const { height, beginDrag } = useSplitHeight()
  const [focusMode, setFocusMode] = useState(false)

  const close = useCallback(() => {
    play('close')
    onClose()
  }, [play, onClose])

  // Esc leaves focus mode first, then closes — same as the concept popup.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (focusMode) setFocusMode(false)
      else close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [close, focusMode])

  // Focus mode covers the whole viewport, so lock the page behind it exactly as
  // the concept popup does.
  useEffect(() => {
    if (!focusMode) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [focusMode])

  return createPortal(
    <aside
      // The concept popup's class carries the sidebar-width offset on desktop
      // and the whole focus-mode layer in index.css, so both panels sit and
      // expand identically. `pdf-viewer-aside` is the one difference: the
      // reader is opened *from* something else — a resource page's Read PDF
      // button, an exam page's syllabus button, the quiz's Question info panel,
      // the Fact Check sheet — so it layers above every host that can open it,
      // in focus mode included. See index.css.
      className="concept-popup-aside pdf-viewer-aside fixed left-0 right-0 bottom-0 z-[135] border-t bg-card text-card-foreground shadow-2xl flex flex-col"
      data-focus={focusMode}
      style={{ height: focusMode ? undefined : `min(${height}px, 100vh)` }}
      // Non-modal, like the concept popup: the shelf behind stays live, so this
      // is a document you read beside the quiz rather than a dialog over it.
      role="complementary"
      aria-label={subtitle ? `${title} — ${subtitle}` : title}
    >
      {!focusMode && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize document panel"
          onMouseDown={e => {
            e.preventDefault()
            beginDrag(e.clientY)
          }}
          onTouchStart={e => {
            if (e.touches[0]) beginDrag(e.touches[0].clientY)
          }}
          className="flex h-4 items-center justify-center cursor-row-resize hover:bg-accent/60 active:bg-accent/80 transition-colors select-none touch-none"
        >
          <GripHorizontal className="h-3 w-6 text-muted-foreground/60" />
        </div>
      )}

      <PdfDocumentView
        url={url}
        title={title}
        subtitle={subtitle}
        initialPage={initialPage}
        focusMode={focusMode}
        controls={
          <>
            <button
              type="button"
              onClick={() => setFocusMode(v => !v)}
              aria-pressed={focusMode}
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title={focusMode ? 'Exit full screen (Esc)' : 'Full screen'}
              aria-label={focusMode ? 'Exit full screen' : 'Full screen'}
            >
              {focusMode ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={close}
              data-sound="none"
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              title="Close"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </>
        }
      />
    </aside>,
    document.body,
  )
}
